// document-types-list.component.ts
import { CommonModule, TitleCasePipe } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import * as _ from 'lodash';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { SkeletonModule } from 'primeng/skeleton';
import { TagModule } from "primeng/tag";
import { TooltipModule } from 'primeng/tooltip';
import { ButtonComponent } from "../../../../@core/components/ui/button/button.component";
import { PopoverComponent } from '../../../../@core/components/ui/popover/popover.component';
import { ToggleComponent } from '../../../../@core/components/ui/toggle/toggle.component';
import { ToastService } from '../../../../@core/services/toast.service';
import { DeleteConfirmationComponent } from "../../../../@core/components/ui/delete-confirmation/delete-confirmation.component";
import { DialogComponent } from '../../../../@core/components/ui/dialog/dialog.component';
import { SearchBarComponent } from '../../../../@shared/components/search-bar/search-bar.component';
import { FilterPipe } from '../../../../pipes/filter.pipe';
import { CheckScreenSize } from '../../../../services/check-screen-size.service';
import { IDocumentType } from '../../../models/settings.model';
import { DocumentTypeService } from '../../../services/document-type.service';
@Component({
    selector: 'app-document-types-list',
    imports: [
        CommonModule,
        FormsModule,
        ButtonModule,
        SkeletonModule,
        TooltipModule,
        PopoverComponent,
        CardModule,
        ToggleComponent,
        SearchBarComponent,
        TitleCasePipe,
        ButtonComponent,
        TagModule,
        DeleteConfirmationComponent,
        DialogComponent,
    ],
    standalone: true,
    templateUrl: './document-types-list.component.html',
    styleUrl: './document-types-list.component.scss'
})
export class DocumentTypesListComponent implements OnInit, OnChanges {
    @ViewChild('docTypeContainer') docTypeContainer!: ElementRef;

    @Input() showAddButton: boolean = false;
    @Input() showMenu: boolean = false;
    @Input() isSelectable: boolean = true;
    @Input() showHeading: boolean = true;

    // Inputs
    @Input() documentTypes: IDocumentType[] = []
    @Input() docTypeLoading: boolean = false;
    // Outputs
    @Output() documentTypeSelected = new EventEmitter<IDocumentType>();
    @Output() refetchDocumentTypes = new EventEmitter<void>();

    // Component state
    docTypeSaveLoading: boolean = false;
    keyPoints: IDocumentType[] = [];
    filteredDocTypes: IDocumentType[] = [];
    selectedDocType: IDocumentType = { _id: '', name: '', summaryDescription: '', comparisionDescription: '' } as IDocumentType;
    searchDocTypes: string = '';
    newDocTypeAdded = false;
    isSmallScreen: boolean = false;

    // Dialog states for internal management
    addDialogVisible = false;
    customSummaryDialog: {
        type: string,
        visible: boolean,
    } = { type: 'Summary', visible: false };
    customComparisonDialogVisible: boolean = false;
    kpiDialog: {
        mode: 'Add' | 'Edit' | 'Delete',
        visible: boolean,
    } = { mode: 'Add', visible: false };

    addEditDocType: Partial<IDocumentType> = { name: '', description: '', summaryDescription: '', comparisionDescription: '', containsForm: false };
    edit: boolean = false;

    // KPI management
    kpiName = '';
    kpiDescription = '';
    selectedKpi: any = undefined;

    // Delete confirmation
    deleteDocTypeDialogVisible: boolean = false;
    selectedDocTypeIdToDelete: string = '';

    filterPipe = new FilterPipe();

    constructor(
        private documentTypeService: DocumentTypeService,
        private toast: ToastService,
        private checkScreenSizeService: CheckScreenSize,
        private route: ActivatedRoute
    ) { }

    ngOnInit() {
        this.isSmallScreen = this.checkScreenSizeService.checkScreenSize();

        this.route.queryParams.subscribe(params => {
            if (params['action'] === 'add-doc-type') {
                this.addDocType();
            }
        });
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['documentTypes'] && this.documentTypes) {
            this.keyPoints = [...this.documentTypes];
            this.filteredDocTypes = [...this.keyPoints];
            if (this.keyPoints.length > 0 && this.isSelectable) {
                const selectedDocType = this.keyPoints.find(item => item._id == this.selectedDocType._id);
                this.selectKeyPoint(selectedDocType ?? this.keyPoints[0]);
            }
        }
    }


    selectKeyPoint(keyPoint: IDocumentType) {
        if (this.selectedDocType == keyPoint) return;
        this.selectedDocType = keyPoint;
        this.documentTypeSelected.emit(keyPoint);
    }

    addDocType() {
        this.addEditDocType = {
            name: '',
            description: '',
            summaryDescription: '',
            comparisionDescription: '',
            containsForm: false
        } as IDocumentType;
        this.edit = false;
        this.addDialogVisible = !this.addDialogVisible;
    }

    editDocType() {
        this.addEditDocType = _.cloneDeep(this.selectedDocType);
        this.edit = true;
        this.addDialogVisible = !this.addDialogVisible;
    }

    saveDocType(from: 'customSummary' | 'customKpis' | 'addDocType' = 'addDocType') {
        if (from == 'addDocType' && (this.addEditDocType.name?.trim()?.length == 0 || this.addEditDocType.description?.trim()?.length == 0)) {
            this.toast.warn("Please fill all the required field");
            return;
        }
        if (!this.edit && from == 'addDocType') {
            const duplicate = this.keyPoints.find(item => item.name == this.addEditDocType.name);
            if (duplicate) {
                this.toast.error('Document Type with given name already exists!');
                return;
            }
        }
        this.docTypeSaveLoading = true;
        this.documentTypeService.addUpdateDocType(this.addEditDocType).subscribe(res => {
            if (res && res.success) {
                this.toast.success(`Document Type ${this.addEditDocType._id ? 'Updated' : 'Added'} Successfully`);
                this.docTypeSaveLoading = false;
                this.addDialogVisible = false;
                this.kpiDialog.visible = false;
                this.customSummaryDialog.visible = false;
                this.customComparisonDialogVisible = false;
                this.selectedDocType = res.data;
                if (!this.addEditDocType._id) {
                    this.newDocTypeAdded = true;
                }
                // this.getDocumentTypes();
                this.refetchDocumentTypes.emit()
            } else {
                this.docTypeSaveLoading = false;
                this.addDialogVisible = !this.addDialogVisible;
                this.kpiDialog.visible = false;
                this.customSummaryDialog.visible = false;
                this.customComparisonDialogVisible = false;
            }
        });
    }

    scrollToBottom() {
        if (this.docTypeContainer) {
            const element = this.docTypeContainer.nativeElement;
            element.scrollTop = element.scrollHeight;
        }
    }

    deleteDocType(id: string) {
        this.selectedDocTypeIdToDelete = id;
        this.deleteDocTypeDialogVisible = true;
    }

    confirmDeleteDocType() {
        this.documentTypeService.deleteDocType(this.selectedDocTypeIdToDelete).subscribe(res => {
            if (res.success) {
                this.toast.success('Document Type Deleted Successfully');
                this.refetchDocumentTypes.emit();
                this.deleteDocTypeDialogVisible = false;
            }
        });
    }

    onDocTypesFilter() {
        this.filteredDocTypes = this.filterPipe.transform(this.keyPoints, 'name', this.searchDocTypes);
    }

    getSkeletonCount(count: number) {
        return Array(count).fill(0);
    }

    handleMenuItemClick(item: { label: string, icon: string }, keyPoint: IDocumentType, popover: PopoverComponent) {
        this.selectedDocType = keyPoint;
        switch (item.label) {
            case 'Edit':
                this.editDocType();
                break;
            case 'Custom Summary':
                this.openCustomSummary(keyPoint);
                break;
            case 'Custom KPI\'s':
                this.openCustomKPI(keyPoint);
                break;
            case 'Delete':
                this.deleteDocType(keyPoint._id);
                break;
        }

        if (popover) {
            popover.hide();
        }
    }

    openCustomSummary(keyPoint: IDocumentType) {
        this.edit = true;
        this.addEditDocType = {
            _id: keyPoint._id,
            name: keyPoint.name,
            description: keyPoint.description,
            summaryDescription: keyPoint.summaryDescription,
            comparisionDescription: keyPoint.comparisionDescription,
            containsForm: keyPoint.containsForm,
            customKpis: keyPoint.customKpis,
            linkedTagTypes: keyPoint.linkedTagTypes
        }
        this.customSummaryDialog.visible = true;
    }

    openCustomKPI(keyPoint: IDocumentType) {
        this.addEditDocType = {
            _id: keyPoint._id,
            name: keyPoint.name,
            description: keyPoint.description,
            summaryDescription: keyPoint.summaryDescription,
            comparisionDescription: keyPoint.comparisionDescription,
            containsForm: keyPoint.containsForm,
            customKpis: keyPoint.customKpis,
            linkedTagTypes: keyPoint.linkedTagTypes
        }
        this.edit = true;
        this.kpiDialog.mode = 'Add';
        this.customComparisonDialogVisible = true;
    }

    openAddKpiDialog(mode: 'Add' | 'Edit' = 'Add', kpi?: any) {
        this.kpiDialog.visible = true;
        this.kpiDialog.mode = mode;
        if (kpi && this.kpiDialog.mode == 'Edit') {
            this.selectedKpi = kpi;
            this.kpiName = kpi.name;
            this.kpiDescription = kpi.description;
        } else {
            this.kpiName = '';
            this.kpiDescription = '';
        }
    }

    saveKpi() {
        if (this.kpiName?.trim()?.length == 0 || this.kpiDescription?.trim()?.length == 0 || this.kpiName == undefined || this.kpiDescription == undefined) {
            return this.toast.warn("Please fill all the required fields");
        }

        if (this.kpiDialog.mode == 'Edit' && this.selectedKpi) {
            this.addEditDocType.customKpis = this.addEditDocType.customKpis?.map(kpi => {
                if (kpi._id === this.selectedKpi?._id) {
                    return { ...kpi, name: this.kpiName, description: this.kpiDescription };
                }
                return kpi;
            });
        } else {
            if (!this.addEditDocType.customKpis) {
                this.addEditDocType.customKpis = [];
            }
            this.addEditDocType.customKpis.push({
                name: this.kpiName,
                description: this.kpiDescription
            });
        }
        this.kpiDialog.visible = false;
    }

    removeKpi(kpi_id: string | undefined) {
        const index = this.addEditDocType.customKpis?.findIndex(kpi => kpi._id === kpi_id);
        if (index !== undefined && index !== -1) {
            this.addEditDocType.customKpis?.splice(index, 1);
        }
        this.kpiDialog.mode = 'Delete';
    }

    onKpisDialogClose() {
        this.kpiName = '';
        this.kpiDescription = '';
    }

    getSelectedDocumentType(): IDocumentType {
        return this.selectedDocType;
    }

    setSelectedDocumentType(docType: IDocumentType) {
        this.selectKeyPoint(docType);
    }

    refreshList() {
        // this.getDocumentTypes();
        this.refetchDocumentTypes.emit()
    }
}
